import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const AlumniNomination = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [myNominations, setMyNominations] = useState([]);
  const [formData, setFormData] = useState({
    position: '',
    nominee_id: '',
    nominee_name: '',
    nominee_membership: '',
    reason: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [alumni, setAlumni] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);

  const positions = [
    { value: 'Chairman', label: 'Chairman' },
    { value: 'Vice Chairman', label: 'Vice Chairman' },
    { value: 'Secretary', label: 'Secretary' },
    { value: 'Treasurer', label: 'Treasurer' },
    { value: 'Member', label: 'Committee Member' },
    { value: 'Patron', label: 'Patron' },
    { value: 'Advisor', label: 'Advisor' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('alumni_token');
    if (!token) {
      navigate('/alumni/login');
      return;
    }
    fetchUserProfile();
    fetchAlumni();
    fetchMyNominations();
  }, [navigate]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = alumni.filter(a =>
        a.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.alumni_membership_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAlumni(filtered);
    } else {
      setFilteredAlumni(alumni);
    }
  }, [searchTerm, alumni]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.get('/alumni/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUserInfo(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.get('/alumni/nomination-list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAlumni(response.data.alumni);
        setFilteredAlumni(response.data.alumni);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
      toast.error('Failed to load alumni list');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyNominations = async () => {
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.get('/alumni/my-nominations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMyNominations(response.data.nominations);
      }
    } catch (error) {
      console.error('Error fetching nominations:', error);
    }
  };

  const selectNominee = (alumnus) => {
    setFormData({
      ...formData,
      nominee_id: alumnus.id,
      nominee_name: alumnus.full_name,
      nominee_membership: alumnus.alumni_membership_number
    });
    setSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.position || !formData.nominee_id || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.reason.length < 20) {
      toast.error('Please provide a reason (minimum 20 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.post('/alumni/nominate', {
        position: formData.position,
        nominee_id: formData.nominee_id,
        reason: formData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Nomination submitted successfully!');
        setFormData({
          position: '',
          nominee_id: '',
          nominee_name: '',
          nominee_membership: '',
          reason: ''
        });
        setSearchTerm('');
        fetchMyNominations();
      } else {
        toast.error(response.data.message || 'Nomination failed');
      }
    } catch (error) {
      console.error('Nomination error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit nomination');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if already nominated (excluding rejected nominations)
  const isPositionAlreadyNominated = (position) => {
    return myNominations.some(n => n.position === position && n.status !== 'rejected');
  };

  // Get position status for summary display
  const getPositionStatus = (position) => {
    const nomination = myNominations.find(n => n.position === position);
    if (nomination) {
      return { nominated: true, status: nomination.status };
    }
    return { nominated: false, status: null };
  };

  // Get badge styling based on status
  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', text: 'Pending ⏳' };
      case 'approved':
        return { color: 'bg-green-100 text-green-800', text: 'Approved ✓' };
      case 'rejected':
        return { color: 'bg-red-100 text-red-800', text: 'Rejected ✗' };
      default:
        return { color: 'bg-gray-100 text-gray-800', text: status };
    }
  };

  // Get status text for summary display
  const getStatusText = (status) => {
    switch(status) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return '';
    }
  };

  // Get status color for summary background
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending':
        return 'bg-yellow-100';
      case 'approved':
        return 'bg-green-100';
      case 'rejected':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  // Get status text color for summary
  const getStatusTextColor = (status) => {
    switch(status) {
      case 'pending':
        return 'text-yellow-600';
      case 'approved':
        return 'text-green-600';
      case 'rejected':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leadership Nominations</h1>
        <p className="text-gray-600 mt-1">
          Welcome, {userInfo?.full_name?.split(' ')[0] || 'Alumni'}! Nominate fellow alumni for leadership positions.
        </p>
      </div>

      {/* My Nominations Summary */}
      <Card className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Your Nominations Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {positions.map(pos => {
              const { nominated, status } = getPositionStatus(pos.value);
              return (
                <div key={pos.value} className="text-center">
                  <div className={`p-2 rounded-lg ${nominated ? getStatusColor(status) : 'bg-gray-100'}`}>
                    <p className="text-xs font-medium">{pos.label}</p>
                    {nominated ? (
                      <span className={`text-xs ${getStatusTextColor(status)} font-medium`}>
                        {getStatusText(status)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Not Nominated</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nomination Form */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Submit Nomination</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position *
              </label>
              <select
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">-- Select Position --</option>
                {positions.map(pos => {
                  const isNominated = isPositionAlreadyNominated(pos.value);
                  return (
                    <option
                      key={pos.value}
                      value={pos.value}
                      disabled={isNominated}
                    >
                      {pos.label} {isNominated ? '(Already Nominated - Pending/Approved)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Note: Rejected nominations can be resubmitted
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Nominee *
              </label>
              <input
                type="text"
                placeholder="Search by name or membership number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {formData.nominee_name && (
                <div className="mt-2 p-2 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    Selected: {formData.nominee_name} ({formData.nominee_membership})
                  </p>
                </div>
              )}
            </div>

            {searchTerm && !formData.nominee_id && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center">Loading...</div>
                ) : filteredAlumni.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No alumni found</div>
                ) : (
                  filteredAlumni.map(alumnus => (
                    <div
                      key={alumnus.id}
                      onClick={() => selectNominee(alumnus)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                    >
                      <p className="font-medium">{alumnus.full_name}</p>
                      <p className="text-sm text-gray-500">{alumnus.alumni_membership_number}</p>
                      <p className="text-xs text-gray-400">{alumnus.department_name}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Nomination *
              </label>
              <textarea
                rows="4"
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Explain why this person is suitable for the position..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 20 characters. {formData.reason.length}/20
              </p>
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? 'Submitting...' : 'Submit Nomination'}
            </Button>
          </form>
        </Card>

        {/* Current Nominations List */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Your Nominations History</h2>
          {myNominations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">You haven't submitted any nominations yet</p>
          ) : (
            <div className="space-y-4">
              {myNominations.map((nom) => {
                const badge = getStatusBadge(nom.status);
                return (
                  <div key={nom.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-primary">{nom.position}</h3>
                        <p className="text-sm mt-1">
                          <strong>Nominee:</strong> {nom.nominee_name}
                        </p>
                        <p className="text-xs text-gray-500">Membership: {nom.nominee_membership}</p>
                        <p className="text-sm mt-2 text-gray-600">{nom.reason}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${badge.color}`}>
                        {badge.text}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Submitted: {new Date(nom.created_at).toLocaleDateString()}
                    </p>
                    {nom.status === 'rejected' && (
                      <div className="mt-2 p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600">
                          <strong>Note:</strong> This nomination was rejected by the administrator.
                          You can submit a new nomination for this position.
                        </p>
                      </div>
                    )}
                    {nom.status === 'approved' && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600">
                          <strong>Congratulations!</strong> Your nomination has been approved.
                        </p>
                      </div>
                    )}
                    {nom.status === 'pending' && (
                      <div className="mt-2 p-2 bg-yellow-50 rounded-lg">
                        <p className="text-xs text-yellow-600">
                          <strong>Under Review:</strong> Your nomination is pending approval.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AlumniNomination;
